import i18n from 'i18next';

import getOrganizationsTransform from './getOrganizationsTransform';
import transformResponse from './transformResponse';

const getFilteredData = ({ data, formState }) =>
  Object.entries(data).reduce(
    (a, [key, value]) => (!formState[key]?.shouldHideField ? { ...a, [key]: value } : a),
    {},
  );

const config = {
  'bulk-update-topics': (data) => ({
    method: 'post',
    url: '/category_follows/bulk_update',
    data,
  }),
  'create-enrollments': ({
    data: { courses, groups_and_users: groupsAndUsers, ...data },
    formState,
  }) => {
    const { groupIds, userIds } = groupsAndUsers.reduce(
      (a, { id, type }) => ({
        ...a,
        groupIds: type === 'group' ? [...a.groupIds, id] : a.groupIds,
        userIds: type === 'user' ? [...a.userIds, id] : a.userIds,
      }),
      { groupIds: [], userIds: [] },
    );

    return {
      data: {
        data: {
          course_ids: courses.map(({ id }) => id),
          group_ids: groupIds,
          user_ids: userIds,
          ...getFilteredData({ data, formState }),
        },
      },
      method: 'post',
      url: '/api/admin/enrollments/bulk_enroll',
    };
  },
  'create-enrollments-import': (data) => ({
    data: { data },
    method: 'post',
    url: '/api/admin/enrollments/csv_imports',
  }),
  'delete-enrollments': (data) => ({
    url: '/api/admin/enrollments',
    data: { data },
    method: 'delete',
  }),
  'enqueue-enrollments-import': (data) => ({
    data: { data },
    method: 'put',
    url: `/api/admin/enrollments/csv_imports/${data.id}/enqueue`,
  }),
  'generate-adhoc-letter': ({ idType, ...data }) => ({
    data: { data },
    method: 'post',
    url: `/api/admin/${idType}s/bulk_letters`,
  }),
  'get-admin-settings': (data) => ({
    transformResponse,
    url: '/api/admin/settings',
    params: { account_id: data?.id },
  }),
  'get-current-profile': () => ({
    transformResponse,
    url: '/api/learner/profile',
  }),
  'get-enrollments-import-settings': ({ type }) => ({
    url: '/api/admin/enrollments/csv_imports/settings',
    params: { type: type || 'enrollment' },
  }),
  'get-current-user': () => ({
    transformResponse,
    url: '/api/learner/who_am_i',
  }),
  'get-dashboards': ({ id, type }) => ({
    transformResponse: [
      ...transformResponse,
      (data) =>
        data.map((dashboard) => {
          const name = i18n.t(dashboard.type);

          return {
            ...dashboard,
            display_name: name,
            ...(id ?? { subtitle: i18n.t('dashboard-subtitle', { name }) }),
          };
        }),
    ],
    params: { dashboardable_id: id, dashboardable_type: type },
    url: '/api/admin/dashboards',
  }),
  'get-enrollment-details': ({ enrollmentId, signal }) => ({
    url: `/api/admin/enrollments/${enrollmentId}`,
    transformResponse,
    signal,
  }),
  'get-groups': (params) => ({
    params,
    transformResponse,
    url: '/api/admin/groups',
  }),
  'get-groups-and-users': (params) => ({
    params,
    transformResponse,
    url: '/api/admin/groups_and_users',
  }),
  'get-learner-settings': () => ({
    transformResponse,
    url: '/api/learner/settings',
  }),
  'get-organizations': () => ({
    transformResponse: [...transformResponse, getOrganizationsTransform],
    url: '/api/admin/organizations/search',
  }),
  'get-admins': ({ searchValue }) => ({
    params: { authority: 'subadmin,superadmin', search_query: searchValue },
    transformResponse,
    url: `/api/admin/groups_and_users/users`,
  }),
  'get-topics': () => ({
    url: '/category_follows?suggested=true&topic_onboarding=true&nested=true',
  }),
  'mark-as-topic-onboarded': ({ userId, authenticityToken }) => ({
    method: 'post',
    url: `/student/users/${userId}/mark_as_topic_onboarded/`,
    headers: { 'X-Csrf-Token': authenticityToken },
  }),
  'get-course-properties': (data) => ({
    url: `/api/admin/courses/${data?.id}/properties`,
  }),
  'report-activity': ({ id, reason, comment }) => ({
    method: 'post',
    url: `/api/learner/courses/${id}/flaggings`,
    data: { data: { reason, comment } },
  }),
  'toggle-dashboard-pin': (data) => ({
    method: data.is_favorited ? 'delete' : 'post',
    url: `/api/admin/dashboards/${data.id}/favorites`,
  }),
  'get-courses': ({ query, type, ...rest }) => ({
    params: { query, type, ...rest },
    url: `/api/admin/courses/search`,
  }),
  'get-ad-hoc-letters': ({ search }) => ({
    params: { per_page: 50, search },
    url: '/api/admin/letters',
  }),
};

export default (name, data) => config[name]?.(data) || {};
